<script setup lang="ts">import { computed as _computed } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { getModule } from 'vuex-module-decorators';
import type { DataOptions } from 'vuetify';
import { computed, ref } from 'vue';
import store from '@/store';
import type {
  CancelablePromise,
  FieldDto,
  AssetSearchCriteria,
} from '@/api';
import {
  AssetPatchDto,
  FieldLevelSearchDto,
  AssetService,
} from '@/api';
import { getAuthorizedAssetsTypeFromEntityType, AssetsTypesEnum } from '@/components/custom-metadata/CustomMetadata';
import { type GenericEditDialogFields } from '@/components/generic-edit-dialog/GenericEditDialog';
import EditDialog from '@/components/generic-edit-dialog/GenericEditDialog.vue';
import authModule from '@/store/modules/auth';
import SiffletifyDataFooter
  from '@/components/siffletify/siffletify-data-footer/siffletify-data-footer.vue';
import i18n from '@/i18n';

interface CatalogResultsProps {
  assets: FieldLevelSearchDto[],
  assetsCount: number,
  hideActions?: boolean,
  shouldTargetBlank?: boolean,
  isEditable?: boolean,
  isSelectable?: boolean,
  isLoading?: boolean,
  searchParams: AssetSearchCriteria
}

type CatalogResultsEmits = {
  (event: 'onFilterChange', option: Partial<DataOptions>): void
  (event: 'onRefreshAssets'): void
}

const auth = getModule(authModule, store);

const props = defineProps({
  assets: null,
  assetsCount: { default: 0 },
  hideActions: { type: Boolean, default: false },
  shouldTargetBlank: { type: Boolean, default: false },
  isEditable: { type: Boolean, default: false },
  isSelectable: { type: Boolean, default: true },
  isLoading: { type: Boolean, default: false },
  searchParams: null,
  value: null
});

const emit = defineEmits(["onFilterChange", "onRefreshAssets", "input"]);

const editDialog = ref<InstanceType<typeof EditDialog> | null>(null);

const { value: selectedAssets } = __MACROS_useVModel(["value", undefined, "input"]);

const filteredAssets = _computed(() => {
  if (props.isEditable) {
    return props.assets.filter((item: FieldLevelSearchDto) => item.entityType !== FieldLevelSearchDto.entityType.DECLARED_ASSET);
  }

  return props.assets;
});

const filteredSelectedAssets = _computed(() => {
  if (props.isEditable) {
    return selectedAssets.value.filter((item: FieldLevelSearchDto) => item.entityType !== FieldLevelSearchDto.entityType.DECLARED_ASSET);
  }

  return selectedAssets.value;
});

const canSelectAny = computed(() => {
  if (props.isEditable) {
    return props.assets.some((asset: FieldLevelSearchDto) => asset.entityType !== FieldLevelSearchDto.entityType.DECLARED_ASSET);
  }
  return props.isSelectable;
});

const canEdit = computed(() => auth.userActions['metadata.asset.catalog-editor']);
const hasNoResults = computed(() => props.assets.length === 0);
const options = computed(() => ({ itemsPerPage: props.searchParams.itemsPerPage, page: props.searchParams.page }));
const from = computed(() => ((options.value.page! - 1) * options.value.itemsPerPage!) + 1);
const to = computed(() => ((options.value.page! - 1) * options.value.itemsPerPage! + options.value.itemsPerPage! <= props.assetsCount ? (options.value.page! - 1) * options.value.itemsPerPage! + options.value.itemsPerPage! : props.assetsCount));
const resultsLabel = computed(() => `${from.value}-${to.value} ${i18n.tc('data-catalog.total_of', props.assetsCount)}`);
const selectedLabel = computed(() => `${filteredSelectedAssets.value.length} ${i18n.t('data-catalog.selected')}`);
const areAllChecked = computed(() => filteredAssets.value.every((asset: FieldLevelSearchDto) => filteredSelectedAssets.value.some((selectedAsset: FieldLevelSearchDto) => asset.id === selectedAsset.id)));
// if at least one asset is selected and not all of them are selected
const isIndeterminate = computed(() => filteredAssets.value.some((asset: FieldLevelSearchDto) => filteredSelectedAssets.value.some((selectedAsset: FieldLevelSearchDto) => asset.id === selectedAsset.id)) && !areAllChecked.value);
const hasSelectionFromOtherPages = computed(() => selectedAssets.value.some((selectedMonitor: FieldLevelSearchDto) => !props.assets.some((monitor: FieldLevelSearchDto) => monitor.id === selectedMonitor.id)));
const showEdit = computed(() => selectedAssets.value.length >= 1);
const computedAssets = computed(() => (props.isLoading ? [] : props.assets));
const uniqueAssetTypes = computed(() => {
  const assetTypes = selectedAssets.value.map((asset: FieldLevelSearchDto) => getAuthorizedAssetsTypeFromEntityType(asset.entityType));
  return [...new Set(assetTypes)];
});
const editDialogFields = computed(() => {
  const fields = ['tags', 'terms', 'description', 'owners'];
  if (uniqueAssetTypes.value.length === 1) fields.push('custom-metadata');
  return fields as GenericEditDialogFields[];
});

const clearSelection = () => {
  selectedAssets.value = [];
};

const updateOptions = (option: Partial<DataOptions>) => {
  emit('onFilterChange', { page: option.page, itemsPerPage: option.itemsPerPage });
};

const updateSelectedAssetsWithNewData = () => {
  // Keep all selected assets that are not in the current page
  const assetsFromOtherPages = selectedAssets.value.filter(
    (selectedItem: FieldLevelSearchDto) => !props.assets.some((item: FieldLevelSearchDto) => item.id === selectedItem.id),
  );

  // Get the updated versions of assets from current page
  const updatedCurrentPageAssets = props.assets.filter(
    (item: FieldLevelSearchDto) => selectedAssets.value.some((selectedItem: FieldLevelSearchDto) => item.id === selectedItem.id),
  );

  // Combine both sets of assets
  selectedAssets.value = [...assetsFromOtherPages, ...updatedCurrentPageAssets];
};

const handleSelect = () => {
  if (!areAllChecked.value || isIndeterminate.value) {
    if (hasSelectionFromOtherPages.value) {
      selectedAssets.value = selectedAssets.value.concat(props.assets);
    } else {
      selectedAssets.value = props.assets;
    }
    return;
  }

  if (areAllChecked.value) {
    selectedAssets.value = selectedAssets.value.filter((selectedMonitor: FieldLevelSearchDto) => !props.assets.some((monitor: FieldLevelSearchDto) => monitor.id === selectedMonitor.id));
  }
};

const isLast = (item: any) => props.assets.indexOf(item) >= options.value.itemsPerPage! - 1;

const editAsset = (asset: FieldLevelSearchDto) => {
  const assetType = getAuthorizedAssetsTypeFromEntityType(asset.entityType);
  if (editDialog.value) {
    editDialog.value.setEntities([asset], assetType);
  }
};

const editAssets = () => {
  updateSelectedAssetsWithNewData();
  // Check if all selected asset types are the same, and pass it to the edit dialog.
  let assetType: AssetsTypesEnum | undefined;
  if (uniqueAssetTypes.value.length === 1) {
    // Array destructuring here would complexify the code readability
    // eslint-disable-next-line prefer-destructuring
    assetType = uniqueAssetTypes.value[0];
  }
  if (editDialog.value) {
    editDialog.value.setEntities(filteredSelectedAssets.value, assetType);
  }
};

const editDialogUpdateHandler = async ({ entities, patches }: { entities: string[], patches: { description?: string, tags?: string[] } | { description?: string, tags?: string[] }[] }) => {
  let promises: CancelablePromise<FieldDto>[];

  if (Array.isArray(entities) && Array.isArray(patches)) {
    // If more than one asset, apply the patch 'ADD' method to custom metadata entries
    const patchOperation = entities.length > 1 ? AssetPatchDto.customMetadataEntriesOperation.ADD : AssetPatchDto.customMetadataEntriesOperation.REPLACE;
    promises = entities.map((urn, index) => {
      const entityPatch = patches[index];
      return AssetService.patchAsset({
        urn,
        requestBody: {
          ...entityPatch,
          customMetadataEntriesOperation: patchOperation,
        },
      });
    });
  } else {
    const requestBody = patches as AssetPatchDto;
    promises = entities.map((urn) => AssetService.patchAsset({ urn, requestBody }));
  }

  await Promise.all(promises);
  await emit('onRefreshAssets');
  updateSelectedAssetsWithNewData();
};
</script>

<template lang="pug">
v-row.mt-1
  v-col( cols="12" )
    v-data-iterator(
      v-model="selectedAssets"
      return-object
      :items="computedAssets"
      :server-items-length="assetsCount"
      :options="options"
      :loading="isLoading"
      hide-default-footer
      data-track="catalog-result-iterator")

      template(v-slot:loading)
        MonitorsResultsPlaceholder

      template(v-slot:no-data)
        NoDataAvailablePlaceholder(
          :text="`${$t('data-catalog.no_results')} “${searchParams.textSearch}”`"
          :secondary-text="`${$t('data-catalog.no_results_secondary')}`" )

      template(v-slot:header)
        v-row( no-gutters style="height: 36px;" )
          v-col.mr-4( align-self="stretch" )
            .d-flex.align-center.full-height( v-if="!hasNoResults && canEdit" )

              v-simple-checkbox.ml-2(
                v-if="isSelectable"
                :class="{ 'disabled': !canSelectAny }"
                color="primary"
                :disabled="!canSelectAny"
                data-track="catalog-results-select-all"
                :value="areAllChecked"
                @input="handleSelect"
                :indeterminate="isIndeterminate" )
              .grey--text.font-weight-medium {{ resultsLabel }}

            .d-flex.align-center.ml-2.full-height( v-if="!canEdit" )
              .grey--text.font-weight-medium {{ resultsLabel }}

          v-col( cols="auto")
            .d-flex
              SButton.mr-2(
                v-if="showEdit"
                icon="icon-dismiss"
                :text="selectedLabel"
                @click="clearSelection"
                color="secondary"
                icon-alignment="right"
                variant="text")

              SButton(
                v-if="!hideActions && showEdit"
                icon="icon-edit"
                :text="$t('data-catalog.edit')"
                @click="editAssets"
                color="secondary"
                variant="outlined"
                :disabled="!canEdit"
                data-track="catalog-results-edit")

        v-row( no-gutters )
          v-col
            v-divider.mt-3

      template(v-slot:item="itemProps")
        CatalogResultsCard(
          :hide-actions="hideActions || itemProps.item.entityType === FieldLevelSearchDto.entityType.DECLARED_ASSET"
          :item-props="itemProps"
          :isLast="isLast(itemProps.item)"
          :shouldTargetBlank="shouldTargetBlank"
          :is-editable="isEditable"
          :is-selectable="isSelectable"
          :search-params="searchParams"
          @edit="editAsset"
        )

      template(v-slot:footer="footer")
        // eslint-disable-next-line vue/valid-v-bind-sync
        SiffletifyDataFooter( :footer.sync="footer" :items-per-page-options="[25, 50, 100]" @update="updateOptions($event)" )

  EditDialog(
    ref="editDialog"
    @update="editDialogUpdateHandler"
    :fields="editDialogFields"
    has-rich-text
    should-fetch-details
  )
</template>

<style lang="scss" scoped>
.disabled {
  cursor: not-allowed;
}

.v-list-item--active::before {
  opacity: 0;
}
</style>
